import type { IntakeFormDefinition, IntakeFormField } from '@aline/common/intake-forms/index';
import { IntakeFormDefinitionSchema } from '@aline/common/intake-forms/index';
import { camelCase } from 'change-case';
import type { LucideIcon } from 'lucide-react';
import {
  AlignLeft,
  AtSign,
  Calendar,
  Check,
  CheckCheck,
  FileInput,
  Hash,
  SquareCheck,
  Type,
} from 'lucide-react';

/**
 * Field types offered in the builder. `email` is sugar for a `text` field with
 * email-format validation; everything else maps directly to a backend field
 * type. `select` is single-choice; `checkbox` is a bare boolean yes/no;
 * `checklist` is a multi-select group of options.
 */
export const BUILDER_FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'date',
  'select',
  'checkbox',
  'checklist',
  'email',
  'fileUpload',
] as const;
export type BuilderFieldType = (typeof BUILDER_FIELD_TYPES)[number];

interface BuilderFieldMeta {
  icon: LucideIcon;
  /** Label shown in the type picker grid and the type dropdown. */
  pickerLabel: string;
  /** Subtitle shown under a saved field in the list. */
  subtitle: string;
}

export const BUILDER_FIELD_META: Record<BuilderFieldType, BuilderFieldMeta> = {
  checkbox: { icon: Check, pickerLabel: 'Checkbox', subtitle: 'Yes/No checkbox' },
  checklist: { icon: SquareCheck, pickerLabel: 'Checklist', subtitle: 'Multi-select checkboxes' },
  date: { icon: Calendar, pickerLabel: 'Date', subtitle: 'Date field' },
  email: { icon: AtSign, pickerLabel: 'Email', subtitle: 'Email field' },
  fileUpload: { icon: FileInput, pickerLabel: 'File Request', subtitle: 'File field' },
  number: { icon: Hash, pickerLabel: 'Number', subtitle: 'Number field' },
  select: { icon: CheckCheck, pickerLabel: 'Select', subtitle: 'Select field' },
  text: { icon: Type, pickerLabel: 'Text', subtitle: 'Text field' },
  textarea: { icon: AlignLeft, pickerLabel: 'Text area', subtitle: 'Text area' },
};

export const INTAKE_FORM_TITLE_TEXT_PLACEHOLDER = 'Untitled Intake Form';
export const INTAKE_FORM_TITLE_INPUT_PLACEHOLDER = 'Enter intake form title...';

export interface DraftSelectOption {
  label: string;
  value: string;
}

/** A field as edited in the builder, before conversion to the persisted shape. */
export interface DraftIntakeField {
  /** Stable submission key; empty for never-saved fields (derived on save). */
  key: string;
  label: string;
  options: DraftSelectOption[];
  required: boolean;
  type: BuilderFieldType;
  helpText?: string;
  /** fileUpload only: max number of files allowed. */
  maxFiles?: number;
  /**
   * The original persisted field, when this draft came from an existing
   * definition. Lets the builder re-emit validation or options the editor
   * cannot fully represent, and lets the renderer keep the correct control
   * when the draft type is sugar (e.g. email → text).
   */
  source?: IntakeFormField;
}

export function createBlankField(type: BuilderFieldType): DraftIntakeField {
  return {
    key: '',
    label: '',
    options: getBuilderFieldNeedsOptions(type) ? [{ label: '', value: '' }] : [],
    required: false,
    type,
  };
}

export function getBuilderFieldNeedsOptions(type: BuilderFieldType): boolean {
  return type === 'select' || type === 'checklist';
}

/**
 * A draft is committable when it has a label and, for option fields, at least one
 * non-empty option — i.e. it will build a valid definition. Shared by the field
 * editor's Save gate and the builder's commit-on-close so they never diverge.
 */
export function getDraftFieldIsComplete(draft: DraftIntakeField): boolean {
  const needsOptions = getBuilderFieldNeedsOptions(draft.type);
  const hasValidOptions = !needsOptions || draft.options.some(option => option.label.trim());
  return Boolean(draft.label.trim()) && hasValidOptions;
}

/**
 * Build the persisted form definition from the builder draft and validate it
 * with the shared schema. Field `name`s are derived from labels (camelCase) and
 * stable once saved (a re-edited field keeps its `key`).
 */
export function buildFormDefinitionFromDraft(
  fields: DraftIntakeField[]
): { definition: IntakeFormDefinition } | { error: string } {
  const rawFields = fields.map((field, index) => {
    const name = field.key || toFieldName(field.label, index);
    const helpText = field.helpText?.trim();
    const common = {
      id: name,
      label: field.label.trim(),
      name,
      order: index,
      required: field.required,
    };
    const commonWithHelpText = helpText ? { ...common, helpText } : common;
    const mappedOptions = field.options
      .filter(option => option.label.trim())
      .map(option => ({
        label: option.label.trim(),
        value: option.value.trim() || option.label.trim(),
      }));

    /**
     * Preserve the original field verbatim when its type was not changed in the
     * builder, so an unrelated edit cannot drop validation the builder cannot
     * represent. Only the builder-editable props are overlaid; a real type
     * change drops `source`'s influence (the equality check fails) and
     * re-emits fresh below.
     */
    if (field.source && toBuilderFieldType(field.source) === field.type) {
      const { helpText: _sourceHelpText, ...sourceWithoutHelpText } = field.source;
      const preserved = {
        ...sourceWithoutHelpText,
        ...commonWithHelpText,
      };
      switch (field.source.type) {
        case 'select':
        case 'checklist':
          return { ...preserved, options: mappedOptions };
        case 'fileUpload':
          return field.maxFiles === undefined
            ? preserved
            : {
                ...preserved,
                validation: { ...field.source.validation, maxFiles: field.maxFiles },
              };
        case 'checkbox':
        case 'date':
        case 'number':
        case 'text':
        case 'textarea':
          return preserved;
      }
    }

    switch (field.type) {
      case 'email':
        return { ...commonWithHelpText, type: 'text', validation: { format: 'email' } };
      case 'fileUpload':
        return field.maxFiles === undefined
          ? { ...commonWithHelpText, type: 'fileUpload' }
          : {
              ...commonWithHelpText,
              type: 'fileUpload',
              validation: { maxFiles: field.maxFiles },
            };
      case 'checklist':
      case 'select':
        return { ...commonWithHelpText, options: mappedOptions, type: field.type };
      case 'checkbox':
      case 'date':
      case 'number':
      case 'text':
      case 'textarea':
        return { ...commonWithHelpText, type: field.type };
    }
  });

  const result = IntakeFormDefinitionSchema.safeParse({ fields: rawFields, version: 1 });
  if (!result.success) {
    const path = result.error.issues[0]?.path.map(String) ?? [];
    if (path.includes('label')) return { error: 'Every field needs a label.' };
    if (path.includes('options'))
      return { error: 'Select and checklist fields need at least one option with a label.' };
    if (path.includes('name')) return { error: 'Each field needs a valid, unique name.' };
    return { error: 'This form has invalid fields. Please review and try again.' };
  }
  return { definition: result.data };
}

/** Parse a persisted form definition (the JSON scalar) back into builder drafts. */
export function draftFieldsFromDefinition(formDefinition: unknown): DraftIntakeField[] {
  const result = IntakeFormDefinitionSchema.safeParse(formDefinition);
  if (!result.success) return [];

  return [...result.data.fields]
    .sort((a, b) => a.order - b.order)
    .map(field => ({
      helpText: field.helpText ?? '',
      key: field.name,
      label: field.label,
      maxFiles: field.type === 'fileUpload' ? field.validation?.maxFiles : undefined,
      options: field.type === 'select' || field.type === 'checklist' ? field.options : [],
      required: field.required,
      source: field,
      type: toBuilderFieldType(field),
    }));
}

function toBuilderFieldType(field: IntakeFormDefinition['fields'][number]): BuilderFieldType {
  switch (field.type) {
    case 'text':
      return field.validation?.format === 'email' ? 'email' : 'text';
    case 'textarea':
      return 'textarea';
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'select':
      return 'select';
    case 'checkbox':
      return 'checkbox';
    case 'checklist':
      return 'checklist';
    case 'fileUpload':
      return 'fileUpload';
  }
}

function toFieldName(label: string, index: number): string {
  const camel = camelCase(label);
  return /^[a-zA-Z]/.test(camel) ? camel : `field${index + 1}`;
}
